import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CenteredColumn } from '@/components/CenteredColumn';
import { FormattedMessage, useIntl } from 'react-intl';
import Header from '@/components/Header/Header';
import { FormSection } from '@/components/FormSection';
import { ApiKeyManagement } from '@/components/User/ApiKeyManagement';
import { FormElement } from '@/components/FormElement';
import { useAuth } from '@/stores/auth';
import { useTheme } from '@/stores/theme';
import { useAnimations } from '@/stores/animations';
import Button from '@/components/core/Button';
import CheckBox from '@/components/core/Checkbox';
import { IconExternalLink, IconLogout } from '@/icons';
import {
  useGetAdminNotificationSettings,
  useSetAdminNotificationSettings,
} from '@/api/trafikportalen';
import { AdminNotificationSettingsDTO } from '@/api/trafikportalen/models/adminNotificationSettingsDTO';
import { useCallback, useEffect, useState } from 'react';
import { toast } from '@/hooks/useToast';
import { ViewPermissions } from '@/components/Auth/ViewPermissions';
import { checkUserPermissions } from '@/components/Auth/checkUserPermissions';
import { DocumentHead } from '@/components/core/DocumentHead';
import { FullsizePage } from '@/components/FullsizePage';
import { RoleIcon } from '@/components/RoleIcon';
import { RoleName } from '@/components/RoleName';

export const Route = createFileRoute('/$tenantId/_layout/user/')({
  component: User,
});

function User() {
  const { userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { mode, isDark, setMode } = useTheme();
  const {
    mode: animationMode,
    enabled: animationsEnabled,
    setMode: setAnimationMode,
  } = useAnimations();

  const updateNotificationsMutation = useSetAdminNotificationSettings();
  const { data: settingsData, isLoading: isLoadingSettings } =
    useGetAdminNotificationSettings({
      query: { enabled: checkUserPermissions({ permission: 'tenant:viewer' }) },
    });

  const [notificationSettings, setNotificationSettings] =
    useState<AdminNotificationSettingsDTO | null>(null);

  const applicationNotificationEnabled =
    notificationSettings?.organization_application_received ?? false;
  const payerCreatedNotificationEnabled =
    notificationSettings?.department_payer_created ?? false;

  useEffect(() => {
    if (settingsData) {
      setNotificationSettings(settingsData);
    }
  }, [settingsData]);

  const handleDarkModeToggle = useCallback(() => {
    setMode(isDark ? 'light' : 'dark');
  }, [isDark, setMode]);

  const handleSystemThemeToggle = useCallback(() => {
    if (mode === 'system') {
      setMode(isDark ? 'dark' : 'light');
    } else {
      setMode('system');
    }
  }, [isDark, mode, setMode]);

  const handleAnimationsToggle = useCallback(() => {
    setAnimationMode(animationsEnabled ? 'disabled' : 'enabled');
  }, [animationsEnabled, setAnimationMode]);

  const handleSystemAnimationsToggle = useCallback(() => {
    if (animationMode === 'system') {
      setAnimationMode(animationsEnabled ? 'enabled' : 'disabled');
    } else {
      setAnimationMode('system');
    }
  }, [animationMode, animationsEnabled, setAnimationMode]);

  const handleNotificationToggle = useCallback(() => {
    const updatedSettings: AdminNotificationSettingsDTO = {
      organization_application_received: !applicationNotificationEnabled,
      department_payer_created: payerCreatedNotificationEnabled,
    };
    updateNotificationsMutation.mutate(
      { data: updatedSettings },
      {
        onSuccess: (data) => {
          setNotificationSettings(data);
          toast.success({
            title: 'Inställningen sparades',
          });
        },
        onError: (error) => {
          console.error(error);
          toast.error({
            title: 'Något gick fel',
            description: error.message,
          });
        },
      },
    );
  }, [
    updateNotificationsMutation,
    applicationNotificationEnabled,
    payerCreatedNotificationEnabled,
  ]);

  const handlePayerNotificationToggle = useCallback(() => {
    const updatedSettings: AdminNotificationSettingsDTO = {
      organization_application_received: applicationNotificationEnabled,
      department_payer_created: !payerCreatedNotificationEnabled,
    };
    updateNotificationsMutation.mutate(
      { data: updatedSettings },
      {
        onSuccess: (data) => {
          setNotificationSettings(data);
          toast.success({
            title: 'Inställningen sparades',
          });
        },
        onError: (error) => {
          console.error(error);
          toast.error({
            title: 'Något gick fel',
            description: error.message,
          });
        },
      },
    );
  }, [
    updateNotificationsMutation,
    applicationNotificationEnabled,
    payerCreatedNotificationEnabled,
  ]);

  return (
    <FullsizePage>
      <DocumentHead
        titleKey="page.user.title"
        descriptionKey="page.user.description"
      />
      <Header
        title={<FormattedMessage defaultMessage="Användarinställningar" />}
      />
      <CenteredColumn>
        <FormSection title={<FormattedMessage defaultMessage="Användare" />}>
          <FormElement
            label={<FormattedMessage defaultMessage="E-postadress" />}
            input={<p>{userProfile?.email}</p>}
          />
          <FormElement
            label={<FormattedMessage defaultMessage="Roll" />}
            input={
              <div className="flex items-center gap-2">
                <RoleIcon roleId={userProfile?.role_id} />
                <RoleName roleId={userProfile?.role_id} />
              </div>
            }
          />
          <Button
            href="https://selfservice.identity.allbin.se/settings"
            target="_blank"
            variant="ghost"
            className="mt-5 w-full"
            endIcon={<IconExternalLink />}
          >
            <FormattedMessage defaultMessage="Ändra profilinställningar" />
          </Button>
        </FormSection>

        <FormSection title={<FormattedMessage defaultMessage="Gränssnitt" />}>
          <CheckBox
            id="dark-mode"
            checked={isDark}
            onClick={handleDarkModeToggle}
            disabled={mode === 'system'}
            label={formatMessage({
              defaultMessage: 'Mörkt läge',
            })}
          />
          <CheckBox
            id="use-system-theme"
            checked={mode === 'system'}
            onClick={handleSystemThemeToggle}
            label={formatMessage({
              defaultMessage: 'Använd systemets tema',
            })}
            description={formatMessage({
              defaultMessage: 'Följ systemets inställning för ljust/mörkt läge',
            })}
          />
          <CheckBox
            id="enable-animations"
            checked={animationsEnabled}
            onClick={handleAnimationsToggle}
            disabled={animationMode === 'system'}
            label={formatMessage({
              defaultMessage: 'Aktivera animationer',
            })}
          />
          <CheckBox
            id="use-system-animations"
            checked={animationMode === 'system'}
            onClick={handleSystemAnimationsToggle}
            label={formatMessage({
              defaultMessage: 'Använd systemets animationsinställning',
            })}
            description={formatMessage({
              defaultMessage: 'Följ systemets inställning för animationer',
            })}
          />
        </FormSection>

        <ViewPermissions permission="tenant:viewer">
          <FormSection
            title={<FormattedMessage defaultMessage="Notifieringar" />}
          >
            <CheckBox
              id="organization-application-notification"
              checked={applicationNotificationEnabled}
              onClick={handleNotificationToggle}
              disabled={
                isLoadingSettings || updateNotificationsMutation.isPending
              }
              label={formatMessage({
                defaultMessage: 'E-post när ny organisation kommer in',
              })}
            />
            <CheckBox
              id="department-payer-notification"
              checked={payerCreatedNotificationEnabled}
              onClick={handlePayerNotificationToggle}
              disabled={
                isLoadingSettings || updateNotificationsMutation.isPending
              }
              label={formatMessage({
                defaultMessage: 'E-post när ny fakturamottagare skapas',
              })}
            />
          </FormSection>
        </ViewPermissions>

        <ApiKeyManagement />

        <Button
          className="w-full"
          onClick={() => {
            logout();
            navigate({ to: '/$tenantId/login' });
          }}
          startIcon={<IconLogout />}
        >
          <FormattedMessage defaultMessage="Logga ut" />
        </Button>
      </CenteredColumn>
    </FullsizePage>
  );
}
